import React, { useState, useEffect } from 'react';

// Material-UI
import { Box, Button, CircularProgress, Container, Link, Paper, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Images
import logoDark from 'images/logo-dark.png';

// Utility
import fetchExpresso from 'utility/fetchExpresso';
import ReactGA from 'react-ga';

export default function ResetPassword(props) {
    const userId = props.match.params.userId;
    const passwordReset = props.match.params.passwordReset;

    const [errorMessage, setErrorMessage] = useState(null);
    const [checkingLink, setCheckingLink] = useState(true);
    const [linkStatus, setLinkStatus] = useState(false);
    const [updateStatus, setUpdateStatus] = useState(false);

    const [passwordState, setPasswordState] = useState({
        newPassword1: '',
        newPassword2: '',
    });

    useEffect(() => {
        fetchExpresso(`/api/auths/check_password_reset`, {
            method: 'POST',
            body: { userId, passwordReset },
        })
            .then((res) => res.json())
            .then((data) => {
                setCheckingLink(false);

                if (data.err === false) {
                    setLinkStatus(true);
                    setErrorMessage(null);
                } else {
                    setLinkStatus(false);
                    setErrorMessage(data.message);
                }
            })
            .catch(() => {
                setCheckingLink(false);
                setErrorMessage('Please check your connection and try again later');
            });
    }, [passwordReset, userId]);

    function onPasswordChange(e) {
        const value = e.target.value;
        setPasswordState({ ...passwordState, [e.target.id]: value });
        setErrorMessage(null);
    }

    function resetPassword() {
        if (passwordState.newPassword1.length < 8) {
            setErrorMessage('New password must be at least eight characters long');
            return;
        }

        if (passwordState.newPassword1 === passwordState.newPassword2) {
            fetchExpresso(`/api/auths/reset_password`, {
                method: 'POST',
                body: {
                    userId,
                    passwordReset,
                    newPassword: passwordState.newPassword1,
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    if (data.err === false) {
                        setUpdateStatus(true);
                        ReactGA.event({
                            category: 'Authentication',
                            action: 'Reset Password',
                            label: 'Unauthenticated',
                        });
                    } else {
                        setErrorMessage(data.message);
                    }
                })
                .catch(() => {
                    setErrorMessage('Please check your connection and try again later');
                });
        } else {
            setErrorMessage('Two new passwords must match');
        }
    }

    function onSubmit(e) {
        e.preventDefault();
        resetPassword();
    }

    return (
        <Page>
            <Header>
                <Container maxWidth="lg">
                    <a href="https://frontdoorplus.com/">
                        <Logo alt="FrontDoor+ Logo" src={logoDark} />
                    </a>
                </Container>
            </Header>

            <Container maxWidth="lg" sx={{ flex: 1 }}>
                <Content>
                    <CardRoot elevation={0}>
                        {checkingLink ? (
                            <LoadingContainer>
                                <Spinner aria-label="Checking reset link" />
                            </LoadingContainer>
                        ) : linkStatus ? (
                            updateStatus ? (
                                <Form>
                                    <CardTitle component="h2">Password updated</CardTitle>
                                    <CardSubtitle>Your password has been successfully changed.</CardSubtitle>
                                    <PrimaryButton
                                        href={`${process.env.REACT_APP_PUBLIC_URL}/login`}
                                        variant="contained"
                                    >
                                        Sign in to your FrontDoor+ Dashboard
                                    </PrimaryButton>
                                </Form>
                            ) : (
                                <Form component="form" noValidate onSubmit={onSubmit}>
                                    <CardTitle component="h2">Choose a new password</CardTitle>
                                    <CardSubtitle>
                                        Enter and confirm your new password below.
                                    </CardSubtitle>

                                    <Label component="label" htmlFor="newPassword1">
                                        New password
                                    </Label>
                                    <AuthTextField
                                        id="newPassword1"
                                        error={Boolean(errorMessage)}
                                        required
                                        type="password"
                                        variant="outlined"
                                        value={passwordState.newPassword1}
                                        onChange={onPasswordChange}
                                        placeholder="At least 8 characters"
                                        fullWidth
                                        inputProps={{ 'aria-label': 'New password' }}
                                    />

                                    <Label component="label" htmlFor="newPassword2">
                                        Confirm new password
                                    </Label>
                                    <AuthTextField
                                        id="newPassword2"
                                        error={Boolean(errorMessage)}
                                        required
                                        type="password"
                                        variant="outlined"
                                        value={passwordState.newPassword2}
                                        onChange={onPasswordChange}
                                        placeholder="Re-enter your new password"
                                        fullWidth
                                        inputProps={{ 'aria-label': 'Confirm new password' }}
                                    />

                                    {errorMessage && <ErrorText>{errorMessage}</ErrorText>}

                                    <PrimaryButton type="submit" variant="contained">
                                        Update Password
                                    </PrimaryButton>
                                </Form>
                            )
                        ) : (
                            <Form>
                                <CardTitle component="h2">Your link has expired</CardTitle>
                                <CardSubtitle>
                                    This probably means that you sent us another password reset request. In that case,
                                    you&apos;ll need to use the newer reset link.
                                </CardSubtitle>
                                {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
                                <SecondaryButton
                                    href={`${process.env.REACT_APP_PUBLIC_URL}/forgotpassword`}
                                    variant="outlined"
                                >
                                    Request a New Reset Link
                                </SecondaryButton>
                            </Form>
                        )}
                    </CardRoot>
                </Content>
            </Container>

            <Footer>
                <Container maxWidth="lg">
                    <FooterInner>
                        <FooterText>&copy; 2026 FrontDoor+</FooterText>
                        <FooterLink href="https://frontdoorplus.com" underline="none">
                            frontdoorplus.com
                        </FooterLink>
                    </FooterInner>
                </Container>
            </Footer>
        </Page>
    );
}

const Page = styled(Box)({
    minHeight: '100vh',
    backgroundColor: '#0b1013',
    color: '#f4f7f8',
    display: 'flex',
    flexDirection: 'column',
});

const Header = styled('header')(({ theme }) => ({
    borderBottom: '1px solid #232b31',
    padding: theme.spacing(2.25, 0),
}));

const Logo = styled('img')({
    display: 'block',
    height: 32,
    maxWidth: '100%',
});

const Content = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: theme.spacing(7),
    paddingBottom: theme.spacing(8),
}));

const CardRoot = styled(Paper)(({ theme }) => ({
    width: '100%',
    maxWidth: 560,
    backgroundColor: '#151b20',
    border: '1px solid #29323a',
    borderRadius: 8,
    color: '#f4f7f8',
    padding: theme.spacing(4.5),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(3),
    },
}));

const Form = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
});

const LoadingContainer = styled(Box)({
    minHeight: 180,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const Spinner = styled(CircularProgress)({
    color: '#34cf91',
});

const CardTitle = styled(Typography)(({ theme }) => ({
    color: '#f8fafb',
    fontSize: 24,
    fontWeight: 800,
    lineHeight: 1.2,
    marginBottom: theme.spacing(1),
}));

const CardSubtitle = styled(Typography)(({ theme }) => ({
    color: '#a8b0b7',
    fontSize: 16,
    lineHeight: 1.4,
    marginBottom: theme.spacing(4),
}));

const Label = styled(Typography)(({ theme }) => ({
    color: '#e6eaed',
    fontSize: 15,
    fontWeight: 800,
    marginBottom: theme.spacing(1),
}));

const AuthTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        color: '#f4f7f8',
        backgroundColor: '#0b1013',
        borderRadius: 4,
        '& fieldset': {
            borderColor: '#29323a',
        },
        '&:hover fieldset': {
            borderColor: '#3a454d',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#34cf91',
        },
        '&.Mui-error fieldset': {
            borderColor: '#f06d6d',
        },
    },
    '& .MuiOutlinedInput-input': {
        padding: theme.spacing(1.55, 1.75),
        fontSize: 16,
        '&::placeholder': {
            color: '#9fa8af',
            opacity: 1,
        },
    },
}));

const ErrorText = styled(Typography)(({ theme }) => ({
    color: '#ff8b8b',
    fontSize: 14,
    fontWeight: 700,
    marginTop: theme.spacing(-1.5),
    marginBottom: theme.spacing(2),
}));

const PrimaryButton = styled(Button)({
    backgroundColor: '#34cf91',
    borderRadius: 4,
    color: '#07100d',
    fontSize: 15,
    fontWeight: 800,
    minHeight: 50,
    textTransform: 'none',
    '&:hover': {
        backgroundColor: '#58dfaa',
    },
});

const SecondaryButton = styled(Button)({
    color: '#f4f7f8',
    borderColor: '#2d363d',
    borderRadius: 4,
    fontSize: 15,
    fontWeight: 700,
    minHeight: 48,
    textTransform: 'none',
    '&:hover': {
        borderColor: '#3c474f',
        backgroundColor: '#151b20',
    },
});

const Footer = styled(Box)(({ theme }) => ({
    borderTop: '1px solid #232b31',
    padding: theme.spacing(2.7, 0),
}));

const FooterInner = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}));

const FooterText = styled(Typography)({
    color: '#9fa8af',
    fontSize: 14,
});

const FooterLink = styled(Link)({
    color: '#9fa8af',
    fontSize: 14,
});
